import React, { useState, useEffect } from 'react';
import { db } from '../../../../firebase';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import Swal from 'sweetalert2';

const FranchisePayoutRequests = () => {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedBankDetails, setSelectedBankDetails] = useState(null);
    const itemsPerPage = 10;

    useEffect(() => {
        fetchPayoutRequests();
    }, []);

    const fetchPayoutRequests = async () => {
        try {
            setLoading(true);
            const snapshot = await getDocs(collection(db, 'franchiseAdmins'));
            const allRequests = [];

            snapshot.forEach((docSnap) => {
                const data = docSnap.data();
                if (data.withdrawalRequests && Array.isArray(data.withdrawalRequests)) {
                    data.withdrawalRequests.forEach(req => {
                        allRequests.push({
                            ...req,
                            adminId: docSnap.id,
                            adminName: data.fullName || 'N/A',
                            adminEmail: data.email || 'N/A',
                            bankDetails: {
                                bankName: data.bankName || 'N/A',
                                accountHolder: data.accountHolderName || 'N/A',
                                accountNumber: data.accountNumber || 'N/A',
                                ifscCode: data.ifscCode || 'N/A'
                            },
                            withdrawnAmount: data.withdrawnAmount || 0,
                            withdrawalRequestsList: data.withdrawalRequests // Reference to update all
                        });
                    });
                }
            });

            // Sort requests by date descending
            allRequests.sort((a, b) => new Date(b.date) - new Date(a.date));
            setRequests(allRequests);
        } catch (error) {
            console.error("Error fetching payout requests:", error);
            Swal.fire('Error', 'Failed to load payout requests.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleApprovePayout = async (request) => {
        const result = await Swal.fire({
            title: 'Confirm Payment?',
            text: `Are you sure you want to mark this request of ₹${request.amount.toLocaleString('en-IN')} as Paid?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#1a7a5e',
            cancelButtonColor: '#64748b',
            confirmButtonText: 'Yes, Mark as Paid'
        });

        if (!result.isConfirmed) return;

        try {
            const updatedRequests = request.withdrawalRequestsList.map(req => 
                req.id === request.id ? { ...req, status: 'Paid' } : req
            );
            const newWithdrawnTotal = request.withdrawnAmount + request.amount;

            const adminRef = doc(db, 'franchiseAdmins', request.adminId);
            await updateDoc(adminRef, {
                withdrawalRequests: updatedRequests,
                withdrawnAmount: newWithdrawnTotal
            });

            // Refresh lists locally
            setRequests(requests.map(req => 
                req.id === request.id 
                    ? { ...req, status: 'Paid', withdrawnAmount: newWithdrawnTotal, withdrawalRequestsList: updatedRequests } 
                    : req
            ));

            Swal.fire({
                title: 'Payout Approved!',
                text: 'The withdrawal request has been marked as Paid.',
                icon: 'success',
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000
            });
        } catch (error) {
            console.error("Error approving payout:", error);
            Swal.fire('Error', 'Failed to approve payout request.', 'error');
        }
    };

    const showBankDetails = (bank) => {
        setSelectedBankDetails(bank);
    };

    const closeBankDetails = () => {
        setSelectedBankDetails(null);
    };

    // Pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentRequests = requests.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(requests.length / itemsPerPage);

    if (loading) {
        return (
            <div className="admin-content-v2" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <p style={{ color: '#64748b' }}>Loading payout requests...</p>
            </div>
        );
    }

    return (
        <div className="admin-content-v2">
            <div className="admin-header-v2" style={{ marginBottom: '2rem' }}>
                <div>
                    <h1 className="admin-title-v2">Franchise Payout Requests</h1>
                    <p className="admin-subtitle-v2">View and process revenue withdrawal requests from franchise partners</p>
                </div>
            </div>

            <div className="table-card-v2">
                <div className="table-responsive">
                    <table className="v2-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                <th style={{ padding: '1rem 1.5rem', textAlign: 'left', color: '#64748b', fontSize: '0.75rem', textTransform: 'uppercase' }}>Partner</th>
                                <th style={{ padding: '1rem 1.5rem', textAlign: 'left', color: '#64748b', fontSize: '0.75rem', textTransform: 'uppercase' }}>Request Date</th>
                                <th style={{ padding: '1rem 1.5rem', textAlign: 'left', color: '#64748b', fontSize: '0.75rem', textTransform: 'uppercase' }}>Amount</th>
                                <th style={{ padding: '1rem 1.5rem', textAlign: 'left', color: '#64748b', fontSize: '0.75rem', textTransform: 'uppercase' }}>Bank Account</th>
                                <th style={{ padding: '1rem 1.5rem', textAlign: 'left', color: '#64748b', fontSize: '0.75rem', textTransform: 'uppercase' }}>Status</th>
                                <th style={{ padding: '1rem 1.5rem', textAlign: 'right', color: '#64748b', fontSize: '0.75rem', textTransform: 'uppercase' }}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {requests.length === 0 ? (
                                <tr>
                                    <td colSpan="6" style={{ padding: '3rem', textAlign: 'center', color: '#64748b' }}>
                                        No payout requests found.
                                    </td>
                                </tr>
                            ) : (
                                currentRequests.map((req, idx) => (
                                    <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                        <td style={{ padding: '1rem 1.5rem' }}>
                                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                <span style={{ fontWeight: 600, color: '#1e293b' }}>{req.adminName}</span>
                                                <span style={{ fontSize: '0.8rem', color: '#64748b' }}>{req.adminEmail}</span>
                                            </div>
                                        </td>
                                        <td style={{ padding: '1rem 1.5rem', color: '#475569' }}>
                                            {new Date(req.date).toLocaleString()}
                                        </td>
                                        <td style={{ padding: '1rem 1.5rem', color: '#1e293b', fontWeight: 700 }}>
                                            ₹{req.amount.toLocaleString('en-IN')}
                                        </td>
                                        <td style={{ padding: '1rem 1.5rem' }}>
                                            <button 
                                                onClick={() => showBankDetails(req.bankDetails)}
                                                style={{ background: '#f1f5f9', border: '1px solid #cbd5e1', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', color: '#475569', fontSize: '0.85rem', fontWeight: 600 }}
                                            >
                                                View Account
                                            </button>
                                        </td>
                                        <td style={{ padding: '1rem 1.5rem' }}>
                                            <span style={{ 
                                                padding: '4px 8px', 
                                                borderRadius: '4px', 
                                                fontSize: '0.75rem', 
                                                fontWeight: 600,
                                                background: req.status === 'Paid' ? '#dcfce7' : '#fef9c3',
                                                color: req.status === 'Paid' ? '#166534' : '#854d0e'
                                            }}>
                                                {req.status}
                                            </span>
                                        </td>
                                        <td style={{ padding: '1rem 1.5rem', textAlign: 'right' }}>
                                            {req.status === 'Pending' ? (
                                                <button 
                                                    onClick={() => handleApprovePayout(req)}
                                                    style={{ background: '#1a7a5e', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' }}
                                                >
                                                    Approve & Pay
                                                </button>
                                            ) : (
                                                <span style={{ color: '#10b981', fontWeight: 600, fontSize: '0.85rem' }}>Completed</span>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {totalPages > 1 && (
                    <div className="pagination-v2" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.5rem', borderTop: '1px solid #e2e8f0', background: '#f8fafc' }}>
                        <button 
                            disabled={currentPage === 1} 
                            onClick={() => setCurrentPage(prev => prev - 1)}
                            style={{ background: 'white', border: '1px solid #cbd5e1', padding: '0.5rem 1rem', borderRadius: '6px', color: '#475569', fontWeight: 600, cursor: currentPage === 1 ? 'not-allowed' : 'pointer', opacity: currentPage === 1 ? 0.5 : 1 }}
                        >
                            Previous
                        </button>
                        <span style={{ fontSize: '0.9rem', color: '#64748b', fontWeight: 500 }}>Page {currentPage} of {totalPages}</span>
                        <button 
                            disabled={currentPage === totalPages} 
                            onClick={() => setCurrentPage(prev => prev + 1)}
                            style={{ background: 'white', border: '1px solid #cbd5e1', padding: '0.5rem 1rem', borderRadius: '6px', color: '#475569', fontWeight: 600, cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', opacity: currentPage === totalPages ? 0.5 : 1 }}
                        >
                            Next
                        </button>
                    </div>
                )}
            </div>

            {/* Bank Details Modal */}
            {selectedBankDetails && (
                <div className="modal-overlay" onClick={closeBankDetails}>
                    <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '400px' }}>
                        <div className="modal-header">
                            <h2>Bank Account Details</h2>
                            <button className="close-btn" onClick={closeBankDetails}>&times;</button>
                        </div>
                        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '1rem', padding: '1.5rem' }}>
                            <div>
                                <label style={{ fontSize: '0.75rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Bank Name</label>
                                <p style={{ margin: '4px 0 0 0', fontWeight: 600, color: '#1e293b' }}>{selectedBankDetails.bankName}</p>
                            </div>
                            <div>
                                <label style={{ fontSize: '0.75rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Account Holder Name</label>
                                <p style={{ margin: '4px 0 0 0', fontWeight: 600, color: '#1e293b' }}>{selectedBankDetails.accountHolder}</p>
                            </div>
                            <div>
                                <label style={{ fontSize: '0.75rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Account Number</label>
                                <p style={{ margin: '4px 0 0 0', fontWeight: 600, color: '#1a7a5e', fontSize: '1.1rem', letterSpacing: '0.5px' }}>{selectedBankDetails.accountNumber}</p>
                            </div>
                            <div>
                                <label style={{ fontSize: '0.75rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>IFSC Code</label>
                                <p style={{ margin: '4px 0 0 0', fontWeight: 600, color: '#1e293b' }}>{selectedBankDetails.ifscCode}</p>
                            </div>
                        </div>
                        <div className="modal-footer" style={{ padding: '1rem 1.5rem' }}>
                            <button className="btn-secondary" onClick={closeBankDetails}>Close</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FranchisePayoutRequests;
